import { Urls } from 'js/src/client';
import type { SupportedFileType } from 'js/src/components/sketch/FileUpload';
import type { NarrationStyle } from 'js/src/data/modals/modals-types';
import Flags from 'js/src/libs/flags';
import { getQueryParam } from 'js/src/libs/querystring';

export const DASHBOARD_SKETCH_STORAGE_KEY = 'dashboard_sketch_transfer';
// Query param on the /app/ destination that sends the staged sketch message
// straight to the chat instead of opening the script composer (the default
// /app/ landing).
export const SKIP_SCRIPT_COMPOSER_PARAM = 'skip_script_composer';
const DATA_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Where dashboard sketch flows should send the user after staging their
 * message. In the sketch-in-creator experience the chat lives in the full
 * editor (/app/) and picks up the staged data on mount; otherwise we use the
 * standalone Sketch page.
 *
 * The /app/ destination carries SKIP_SCRIPT_COMPOSER_PARAM so the staged message
 * goes straight to the chat rather than opening the script composer (which is
 * the default landing for /app/).
 */
export function getSketchDestinationUrl(): string {
    return Flags.canUseToolbox() ? `${Urls.APP}?${SKIP_SCRIPT_COMPOSER_PARAM}=true` : Urls.SKETCH;
}

export interface SerializedDocument {
    id: string;
    name: string;
    type: SupportedFileType;
    url: string;
    text: string;
}

export interface SerializedMedia {
    id: string;
    name: string;
    type: SupportedFileType;
    uuid: string;
}

export interface SerializedVoiceover {
    id: string;
    name: string;
    type: SupportedFileType;
    uuid: string;
}

export interface DashboardSketchTransferData {
    version: 1;
    timestamp: number;
    messageText: string;
    documents: SerializedDocument[];
    media: SerializedMedia[];
    voiceovers?: SerializedVoiceover[];
    narrationStyle?: NarrationStyle;
    recipeName?: string;
}

export function saveDashboardSketchData(
    data: Omit<DashboardSketchTransferData, 'version' | 'timestamp'>,
): boolean {
    try {
        const fullData: DashboardSketchTransferData = {
            ...data,
            version: 1,
            timestamp: Date.now(),
        };
        sessionStorage.setItem(DASHBOARD_SKETCH_STORAGE_KEY, JSON.stringify(fullData));
        return true;
    } catch (error) {
        // Storage quota exceeded or other error
        return false;
    }
}

export function getDashboardSketchData(): DashboardSketchTransferData | null {
    try {
        const rawData = sessionStorage.getItem(DASHBOARD_SKETCH_STORAGE_KEY);
        if (!rawData) {
            return null;
        }

        const data: DashboardSketchTransferData = JSON.parse(rawData);

        // Validate version
        if (data.version !== 1) {
            clearDashboardSketchData();
            return null;
        }

        // Check expiry
        if (Date.now() - data.timestamp > DATA_EXPIRY_MS) {
            clearDashboardSketchData();
            return null;
        }

        return data;
    } catch (error) {
        clearDashboardSketchData();
        return null;
    }
}

/**
 * The message text staged by the dashboard that is about to be sent to the chat
 * on mount, or null if this page load isn't a sketch handoff.
 *
 * Read synchronously during render so the loading UI can show the user's own
 * message from the very first frame instead of an empty "what are we creating
 * today?" prompt they already answered on the dashboard. Peeks only — the send
 * path still owns consuming and clearing the staged data.
 */
export function peekPendingSketchHandoffText(): string | null {
    if (getQueryParam(SKIP_SCRIPT_COMPOSER_PARAM) !== 'true') {
        return null;
    }
    const text = getDashboardSketchData()?.messageText?.trim();
    return text ? text : null;
}

export function clearDashboardSketchData(): void {
    try {
        sessionStorage.removeItem(DASHBOARD_SKETCH_STORAGE_KEY);
    } catch (error) {
        // Ignore errors when clearing
    }
}

export const CREATE_VIEW_SEED_STORAGE_KEY = 'dashboard_create_seed';

/**
 * A prompt staged for the Create page's chat input to open with, rather than
 * sent on arrival. Kept apart from the sketch handoff above: that one carries a
 * message the user already submitted and auto-sends it at the destination,
 * whereas this one is a starting point the user is still expected to edit and
 * send themselves.
 */
export interface CreateViewSeed {
    text: string;
    /** Carried through so the video still lands in the project it was started from. */
    projectId?: number | null;
}

export function saveCreateViewSeed(seed: CreateViewSeed): boolean {
    try {
        sessionStorage.setItem(
            CREATE_VIEW_SEED_STORAGE_KEY,
            JSON.stringify({ version: 1, timestamp: Date.now(), ...seed }),
        );
        return true;
    } catch (error) {
        return false;
    }
}

/**
 * Reads the staged Create-page prompt and clears it, so a reload or a later
 * visit starts from an empty input instead of re-seeding a prompt the user has
 * already moved on from.
 */
export function consumeCreateViewSeed(): CreateViewSeed | null {
    try {
        const raw = sessionStorage.getItem(CREATE_VIEW_SEED_STORAGE_KEY);
        sessionStorage.removeItem(CREATE_VIEW_SEED_STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const data = JSON.parse(raw);
        if (data?.version !== 1 || Date.now() - data.timestamp > DATA_EXPIRY_MS) {
            return null;
        }
        if (typeof data.text !== 'string' || !data.text) {
            return null;
        }
        return { text: data.text, projectId: data.projectId ?? null };
    } catch (error) {
        return null;
    }
}
